var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { AuthenticationResponseResult } from '../AuthenticationResponseResult';
import { AuthenticationResponseType } from '../AuthenticationResponseType';
import { AuthenticationType } from '../AuthenticationType';
import { MessageBoxType, showCertificateScreenAsync, showMessageBoxAsync } from '@shared/utils/MessageBoxUtils';
import $ from 'jquery';
import { InformationMessageType } from '../../Enums/Constants';
import { ConnnectionResponseCode } from '../../Enums/ConnectionEnum';
import { UserWebStorageInstance } from '../../../user-web-storage';
import { AsyncQueue } from '@shared/utils/AsyncQueue';
export var CapitalFuturesAuthenticationServiceType;
(function (CapitalFuturesAuthenticationServiceType) {
    CapitalFuturesAuthenticationServiceType[CapitalFuturesAuthenticationServiceType["Standalone"] = 0] = "Standalone";
    CapitalFuturesAuthenticationServiceType[CapitalFuturesAuthenticationServiceType["CFDesktop"] = 1] = "CFDesktop";
    CapitalFuturesAuthenticationServiceType[CapitalFuturesAuthenticationServiceType["Apps"] = 2] = "Apps";
})(CapitalFuturesAuthenticationServiceType || (CapitalFuturesAuthenticationServiceType = {}));
export class CapitalFuturesCertificateDetails {
}
export class CapitalFuturesCertificateResponse {
    constructor(certificate, responseCode, responseMessage) {
        this.certificate = certificate;
        this.responseCode = responseCode;
        this.responseMessage = responseMessage;
    }
}
export class CapitalFuturesAuthenticationService {
    get type() {
        return AuthenticationType.CapitalFutures;
    }
    get certificateLinkText() {
        return Resources.getResource('property.ca.info');
    }
    constructor(scriptUrl, registrationLink) {
        this._asyncQueue = new AsyncQueue();
        this._scriptUrl = scriptUrl;
        this._registrationLink = registrationLink;
        if (isValidString(scriptUrl) && isValidURL(scriptUrl)) {
            const parsedUrl = new URL(scriptUrl);
            this._serviceAddress = parsedUrl.origin;
        }
        const queryParams = new URLSearchParams(location.search.substring(1));
        this._source = queryParams.get('source');
        switch (this._source) {
            case 'Desk':
                this._serviceType = CapitalFuturesAuthenticationServiceType.CFDesktop;
                break;
            case 'Mob':
                this._serviceType = CapitalFuturesAuthenticationServiceType.Apps;
                break;
            default:
                this._serviceType = CapitalFuturesAuthenticationServiceType.Standalone;
                break;
        }
        if (this._serviceType === CapitalFuturesAuthenticationServiceType.Standalone) {
            const script = document.createElement('script');
            script.src = this._scriptUrl;
            script.type = 'text/javascript';
            document.head.appendChild(script);
        }
    }
    // #region IAuthenticationCertificateLoginService
    showCertificate(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const certificateInfo = yield this.getCertificateInfo(id);
            const certificateScreenParams = {
                headerText: Resources.getResource('property.ca.info'),
                certificateInfoParams: certificateInfo.Result,
                cancelButtonText: Resources.getResource('property.Delete')
            };
            const result = yield showCertificateScreenAsync(certificateScreenParams);
            if (!result) {
                const messageBoxParams = {
                    message: Resources.getResource('property.ca.deleteConfirmation'),
                    type: MessageBoxType.Question,
                    okText: Resources.getResource('general.messageBox.ok'),
                    cancelText: Resources.getResource('general.messageBox.cancel')
                };
                const deleteMessageResult = yield showMessageBoxAsync(messageBoxParams);
                if (deleteMessageResult) {
                    void this.deleteCertificate(id);
                }
            }
        });
    }
    checkCertificate(id) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (this._serviceType) {
                case CapitalFuturesAuthenticationServiceType.Standalone:
                    return yield this.checkCertificateStandalone(id);
                default:
                    return new AuthenticationResponseResult(AuthenticationResponseType.Success, {
                        informationMessage: null,
                        result: ''
                    });
            }
        });
    }
    deleteCertificate(id) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (this._serviceType) {
                case CapitalFuturesAuthenticationServiceType.Standalone:
                    this.deleteCertificateStandalone(id);
                    break;
                default:
                    break;
            }
        });
    }
    // #endregion
    // #region IAuthenticationLoginTokenService
    isInputMode() {
        return this._serviceType === CapitalFuturesAuthenticationServiceType.Standalone;
    }
    processToken() {
        const queryParams = new URLSearchParams(location.search.substring(1));
        let token = queryParams.get('token');
        if (isValidString(token)) {
            if (token.startsWith('{') && token.endsWith('}')) {
                token = token.substring(1, token.length - 1);
            }
            return new AuthenticationResponseResult(AuthenticationResponseType.Success, token);
        }
        else {
            return new AuthenticationResponseResult(AuthenticationResponseType.Error, '');
        }
    }
    processConnectionData(connectionData, token) {
        const queryParams = new URLSearchParams(location.search.substring(1));
        const login = queryParams.get('login');
        const source = queryParams.get('source');
        connectionData.TokenID = token;
        connectionData.login = login;
        connectionData.ipURL = UserWebStorageInstance.ipAddress;
        connectionData.ExternalResourse = this.type;
        connectionData.customerNo = source;
    }
    processLoginRejection(code) {
        const tokenResponse = this.processToken();
        if (tokenResponse.ResponseType !== AuthenticationResponseType.Success) {
            return false;
        }
        const registrationLink = this._registrationLink;
        if (!isValidString(registrationLink)) {
            return false;
        }
        if (code === ConnnectionResponseCode.SAFE_DISCONECT) {
            this.openWindow(registrationLink);
            return true;
        }
        else {
            return false;
        }
    }
    // #endregion
    // #region IAuthenticationTradingService
    isAllowTrading(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const certificateResponse = yield this._asyncQueue.enqueue(() => __awaiter(this, void 0, void 0, function* () { return yield this.getCertificate(id); }));
            const certificate = certificateResponse.certificate;
            if (isNullOrUndefined(certificate)) {
                const informationMessage = {
                    type: InformationMessageType.TradeCA,
                    message: this.getCertificateTradeFailedInformationMessage(id)
                };
                const error = `Code: ${certificateResponse.responseCode}; ${certificateResponse.responseMessage}`;
                return new AuthenticationResponseResult(AuthenticationResponseType.Error, {
                    informationMessage,
                    result: error
                });
            }
            else {
                const informationMessage = {
                    type: InformationMessageType.TradeCA,
                    message: this.getCertificateTradeSuccessInformationMessage(certificate, id)
                };
                return new AuthenticationResponseResult(AuthenticationResponseType.Success, {
                    informationMessage,
                    result: ''
                });
            }
        });
    }
    // #endregion
    // #region Standalone
    getCertificateStandalone(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawData = 'Login_' + id + '_futures_ev';
            const certificateResponse = yield new Promise((resolve, reject) => {
                try {
                    SignWebCA(id, rawData, (code, msg, token, data) => {
                        if (code === 0) {
                            resolve(new CapitalFuturesCertificateResponse(JSON.parse(data), code, msg));
                        }
                        else {
                            resolve(new CapitalFuturesCertificateResponse(null, code, msg));
                        }
                    });
                }
                catch (_a) {
                    resolve(new CapitalFuturesCertificateResponse(null, -1, 'Unexpected error in SignWebCA'));
                }
            });
            return certificateResponse;
        });
    }
    checkCertificateStandalone(id) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const dataObject = yield new Promise((resolve, reject) => {
                    CheckWebCAState(id, (code, msg, token, data) => {
                        if (code === 0 && data) {
                            resolve(JSON.parse(data));
                        }
                        else {
                            const dataObject = !isNullOrUndefined(data) ? JSON.parse(data) : null;
                            const error = {
                                name: !isNullOrUndefined(dataObject === null || dataObject === void 0 ? void 0 : dataObject.suggestAction) ? dataObject.suggestAction : 'None',
                                message: `CA Error: errCode:${code};errMsg:${msg}`
                            };
                            reject(error);
                        }
                    });
                });
                const suggestAction = dataObject.suggestAction;
                if (suggestAction === 'ApplyCert') {
                    console.log('Apply for the CA');
                    const message = Resources.getResource('property.ca.noCA');
                    const apply = Resources.getResource('property.ca.applyCA');
                    const cancel = Resources.getResource('general.messageBox.cancel');
                    const messageBoxParameters = {
                        title: '',
                        message,
                        okText: apply,
                        cancelText: cancel,
                        type: MessageBoxType.Info
                    };
                    const result = yield showMessageBoxAsync(messageBoxParameters);
                    if (result) {
                        const applyUrl = `${this._serviceAddress}/Password/ReissueCert.aspx`;
                        const form = $('<form/>').attr('action', applyUrl).attr('method', 'post').appendTo($('body'));
                        void $('<input/>').attr('type', 'hidden').attr('name', 'IDNO').val(id).appendTo(form);
                        void $('<input/>').attr('type', 'hidden').attr('name', 'Source').val('L1').appendTo(form); // Input the platform source. please use "futures_ev" as default value.
                        void $('<input/>').attr('type', 'hidden').attr('name', 'ReturnWord').val('返回').appendTo(form);
                        void $('<input/>')
                            .attr('type', 'hidden')
                            .attr('name', 'ReturnUrl')
                            .val(location.href)
                            .appendTo(form);
                        form.submit();
                        return new AuthenticationResponseResult(AuthenticationResponseType.Error, {
                            informationMessage: null,
                            result: ''
                        });
                    }
                    else {
                        const error = {
                            name: suggestAction,
                            message: 'CA Error: errCode:5010;errMsg:SelectSignerEx 無憑證'
                        };
                        throw error;
                    }
                }
                else if (suggestAction === 'RenewCert') {
                    console.log('Extend the CA');
                    const certificateResponse = yield this.getCertificate(id);
                    const certificate = certificateResponse.certificate;
                    const certificateInfo = this.generateCertificateInfo(certificate);
                    const message = `${Resources.getResource('property.ca.expiredCA')}\n${this.concatSertificateInfo(certificateInfo)}`;
                    const apply = Resources.getResource('property.ca.extedCA');
                    const cancel = Resources.getResource('general.messageBox.cancel');
                    const messageBoxParameters = {
                        title: '',
                        message,
                        okText: apply,
                        cancelText: cancel,
                        type: MessageBoxType.Info,
                        isPreWrap: true,
                        messageAlignment: 'left'
                    };
                    const result = yield showMessageBoxAsync(messageBoxParameters);
                    if (result) {
                        const RenewUrl = `${this._serviceAddress}/Password/ReNewCert.aspx`;
                        const form = $('<form/>').attr('action', RenewUrl).attr('method', 'post').appendTo($('body'));
                        void $('<input/>').attr('type', 'hidden').attr('name', 'IDNO').val(id).appendTo(form);
                        void $('<input/>').attr('type', 'hidden').attr('name', 'Source').val('L1').appendTo(form); // Input the platform source. please use "futures_ev" as default value.
                        void $('<input/>').attr('type', 'hidden').attr('name', 'ReturnWord').val('返回').appendTo(form);
                        void $('<input/>')
                            .attr('type', 'hidden')
                            .attr('name', 'ReturnUrl')
                            .val(location.href)
                            .appendTo(form);
                        form.submit();
                    }
                    else {
                        const informationMessage = {
                            type: InformationMessageType.LoginCA,
                            message: this.getCertificateLoginSuccessInformationMessage(certificate, id)
                        };
                        return new AuthenticationResponseResult(AuthenticationResponseType.Success, {
                            informationMessage,
                            result: ''
                        });
                    }
                }
                else {
                    const certificateResponse = yield this.getCertificate(id);
                    const certificate = certificateResponse.certificate;
                    if (!isNullOrUndefined(certificate)) {
                        console.log('Successfully check the CA');
                        const informationMessage = {
                            type: InformationMessageType.LoginCA,
                            message: this.getCertificateLoginSuccessInformationMessage(certificate, id)
                        };
                        return new AuthenticationResponseResult(AuthenticationResponseType.Success, {
                            informationMessage,
                            result: ''
                        });
                    }
                    else {
                        const error = {
                            name: suggestAction,
                            message: `CA Error: errCode:${certificateResponse.responseCode};errMsg:${certificateResponse.responseMessage}`
                        };
                        throw error;
                    }
                }
            }
            catch (error) {
                const message = error.message;
                const messageBoxParameters = {
                    title: '',
                    message,
                    okText: Resources.getResource('general.messageBox.ok'),
                    cancelText: '',
                    type: MessageBoxType.Error
                };
                yield showMessageBoxAsync(messageBoxParameters);
                let informationMessage = null;
                if (isValidString(error.name) && error.name !== 'None') {
                    informationMessage = {
                        type: InformationMessageType.LoginCA,
                        message: this.getCertificateLoginFailedInformationMessage(error.name)
                    };
                }
                return new AuthenticationResponseResult(AuthenticationResponseType.Error, {
                    informationMessage,
                    result: error.message
                });
            }
        });
    }
    deleteCertificateStandalone(id) {
        this.openWindow(`${this._serviceAddress}/Password/DelCert.aspx`);
    }
    // #endregion
    // #region CFDesktop
    getCertificateCFDesktop(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield new Promise((resolve) => {
                window.AfterSign = (data) => {
                    resolve(JSON.parse(data));
                };
                const rawData = 'Sign_' + id + '_futures_ev';
                window.preventUnload = true;
                location.href = '!!TESign!!id=' + id + '&rawdata=' + rawData + '&callbackFunc=AfterSign';
            });
            const details = {
                sn: result.CertID,
                cn: result.CN,
                sign: result.Sign,
                ntb: undefined,
                nta: undefined,
                certtype: '',
                rawdata: '',
                verify_P7: '',
                Source: ''
            };
            window.AfterSign = undefined;
            return new CapitalFuturesCertificateResponse(details, 0, '');
        });
    }
    // #endregion
    // #region Apps
    getCertificateApps(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const checkCertResult = yield new Promise((resolve) => {
                window.appCheckCert = (type, rcode, msg) => {
                    const response = new CapitalFuturesCertificateResponse(null, Number(rcode), msg);
                    resolve(response);
                };
                window.preventUnload = true;
                location.href = '!!checkCert!!id=' + id + '&broker=' + '' + '&account=' + '' + '&callbackFunc=appCheckCert';
            });
            window.appCheckCert = undefined;
            if (checkCertResult.responseCode !== 0) {
                return checkCertResult;
            }
            const signCertResult = yield new Promise((resolve) => {
                window.appSignCert = (type, rcode, msg) => {
                    if (rcode === '0') {
                        const certificate = JSON.parse(msg);
                        resolve(new CapitalFuturesCertificateResponse(certificate, Number(rcode), msg));
                    }
                    else {
                        resolve(new CapitalFuturesCertificateResponse(null, Number(rcode), msg));
                    }
                };
                const rawdata = 'Login_' + id + '_futures_ev';
                window.preventUnload = true;
                location.href = '!!signCert!!id=' + id + '&rawdata=' + rawdata + '&callbackFunc=appSignCert';
            });
            window.appSignCert = undefined;
            return signCertResult;
        });
    }
    // #endregion
    // #region Helpers
    getCertificateInfo(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const certificateResponse = yield this.getCertificate(id);
            if (!isNullOrUndefined(certificateResponse.certificate)) {
                return new AuthenticationResponseResult(AuthenticationResponseType.Success, this.generateCertificateInfo(certificateResponse.certificate));
            }
            else {
                const emptyCertificate = {
                    sn: '',
                    cn: '',
                    ntb: undefined,
                    nta: undefined,
                    sign: '',
                    certtype: '',
                    rawdata: '',
                    verify_P7: '',
                    Source: ''
                };
                return new AuthenticationResponseResult(AuthenticationResponseType.Error, this.generateCertificateInfo(emptyCertificate));
            }
        });
    }
    getCertificate(id) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (this._serviceType) {
                case CapitalFuturesAuthenticationServiceType.Standalone:
                    return yield this.getCertificateStandalone(id);
                case CapitalFuturesAuthenticationServiceType.CFDesktop:
                    return yield this.getCertificateCFDesktop(id);
                case CapitalFuturesAuthenticationServiceType.Apps:
                    return yield this.getCertificateApps(id);
                default:
                    throw new Error('Invalid service type');
            }
        });
    }
    generateCertificateInfo(certificate) {
        const result = {
            [Resources.getResource('property.ca.caSerialNumber')]: certificate.sn,
            [Resources.getResource('property.ca.caSubject')]: certificate.cn
        };
        switch (this._serviceType) {
            case CapitalFuturesAuthenticationServiceType.Standalone:
                result[Resources.getResource('property.ca.startDate')] = this.formatDate(this.parseDate(certificate.ntb));
                result[Resources.getResource('property.ca.endDate')] = this.formatDate(this.parseDate(certificate.nta));
                break;
            case CapitalFuturesAuthenticationServiceType.CFDesktop:
                result[Resources.getResource('property.ca.sign')] = certificate.sign;
                break;
            default:
                break;
        }
        return result;
    }
    concatSertificateInfo(entries) {
        let result = '';
        for (const key in entries) {
            result += `${key}: ${entries[key]}\n`;
        }
        return result;
    }
    parseDate(str) {
        const dateRegEx = /^\d{14}$/;
        if (!dateRegEx.test(str)) {
            return undefined;
        }
        const year = Number(str.slice(0, 4));
        const month = Number(str.slice(4, 6)) - 1;
        const day = Number(str.slice(6, 8));
        const hours = Number(str.slice(8, 10));
        const minutes = Number(str.slice(10, 12));
        const seconds = Number(str.slice(12, 14));
        return new Date(year, month, day, hours, minutes, seconds);
    }
    formatDate(date) {
        if (isNullOrUndefined(date)) {
            return '0001-01-01 00:00:00';
        }
        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    getCertificateLoginSuccessInformationMessage(certificate, login) {
        const cn = certificate.cn;
        const sn = certificate.sn;
        const rawData = `$Login_${login}_futures_ev`;
        return `103;${cn};${sn};${rawData} check: ok`;
    }
    getCertificateLoginFailedInformationMessage(action) {
        const cn = '';
        const sn = '';
        return `103;${cn};${sn};${action}: failed`;
    }
    getCertificateTradeSuccessInformationMessage(certificate, login) {
        const cn = certificate.cn;
        const sn = certificate.sn;
        const rawData = `$Account_${login}_futures_ev`;
        return `103;${cn};${sn};${rawData} check: ok`;
    }
    getCertificateTradeFailedInformationMessage(login) {
        const cn = '';
        const sn = '';
        const rawData = `$Account_${login}_futures_ev`;
        return `103;${cn};${sn};${rawData} check: failed`;
    }
    openWindow(url) {
        if (this._serviceType === CapitalFuturesAuthenticationServiceType.Apps) {
            window.preventUnload = true;
            location.href = '!!open!!strUrl=' + encodeURIComponent(url) + '&inApp=' + 'N';
        }
        else {
            openWindow(url);
        }
    }
}
